import type { FC } from 'react'
import type { Reward, UserRewardItem } from 'types/rewards'

import {
  Box,
  Button,
  CircularProgress,
  Grid2 as Grid,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material'

import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import SimpleTitle from 'components/SimpleTitle'
import useAuth from 'hooks/useAuth'
import { useFetchCategoriesWithRewards } from 'hooks/useRewards'
import {
  useFetchUserRewards,
  useSaveUserRewards,
  invalidateUserRewardsCache,
  invalidateEmployeeRewardsSummariesCache,
} from 'hooks/useUserRewards'
import { usePeriods } from 'hooks/usePeriods'

import { useSnackbar } from 'notistack'
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getRootOrganizationId } from 'types/user-profile'
import { findPeriodById, createPeriodToRootPeriodMap } from 'types/periods'
import { PackageCategoryCard } from '../components/PackageCategoryCard'
import { ReviewCategoryCard } from '../components/ReviewCategoryCard'

interface RewardItemState {
  rewardId: string
  rewardName: string
  categoryId: string
  categoryName: string
  categoryDisplayOrder: number
  rewardDisplayOrder: number
  selected: boolean
  companyContribution: number
  employeeContribution: number
  supportsEmployeeContribution: boolean
}

const steps = ['Select Rewards', 'Review']

const EditEmployeeRewards: FC = () => {
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { userId, periodId } = useParams<{ userId: string; periodId: string }>()

  const { token, profile } = useAuth()
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')

  const [activeStep, setActiveStep] = useState(0)
  const [rewardItems, setRewardItems] = useState<Map<string, RewardItemState>>(new Map())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // Get periods to find rootPeriodId
  const { periods: periodsResponse } = usePeriods(token!, rootOrganizationId, !!rootOrganizationId && !!token)
  const periodsData = periodsResponse?.periods || []

  // Get period info
  const periodInfo = useMemo(() => {
    if (!periodId || !periodsData.length) return null
    const period = findPeriodById(periodsData, periodId)
    return period
  }, [periodId, periodsData])

  const rootPeriodId = useMemo(() => {
    if (!periodId || !periodsData.length) return ''
    const periodToRootMap = createPeriodToRootPeriodMap(periodsData)
    return periodToRootMap[periodId] || ''
  }, [periodId, periodsData])

  // Fetch categories with rewards
  const { categoriesWithRewards, isLoading: isLoadingRewards } = useFetchCategoriesWithRewards(
    rootOrganizationId,
    token!,
    !!rootOrganizationId && !!token
  )

  // Fetch existing user rewards
  const {
    userRewards,
    packageInfo,
    isLoading: isLoadingUserRewards,
  } = useFetchUserRewards(userId || '', periodId || '', token!, !!userId && !!periodId && !!token)

  const { saveUserRewards } = useSaveUserRewards(token!)

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  // Build flat list of rewards from the package only
  const allRewards = useMemo(() => {
    if (!categoriesWithRewards || !packageInfo?.rewards) return []

    // Create a Set of reward IDs from the package for quick lookup
    const packageRewardIds = new Set(packageInfo.rewards.map((r) => r.rewardId))

    const rewards: (Reward & { categoryId: string; categoryName: string; categoryDisplayOrder: number })[] = []
    categoriesWithRewards.forEach((cat) => {
      cat.rewards.forEach((reward) => {
        // Only include rewards that are in the package
        if (packageRewardIds.has(reward.id!)) {
          rewards.push({
            ...reward,
            categoryId: cat.id!,
            categoryName: cat.name,
            categoryDisplayOrder: cat.displayOrder,
          })
        }
      })
    })
    // Sort by category display order, then by reward display order
    rewards.sort((a, b) => {
      const catCompare = a.categoryDisplayOrder - b.categoryDisplayOrder
      if (catCompare !== 0) return catCompare
      return (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    })
    return rewards
  }, [categoriesWithRewards, packageInfo])

  // Initialize form with package rewards (all selected by default)
  useEffect(() => {
    if (allRewards.length > 0 && !isInitialized && !isLoadingUserRewards && packageInfo) {
      const items = new Map<string, RewardItemState>()
      allRewards.forEach((reward) => {
        const existingItem = userRewards?.find((r) => r.rewardId === reward.id)
        const packageItem = packageInfo.rewards?.find((r) => r.rewardId === reward.id)

        items.set(reward.id!, {
          rewardId: reward.id!,
          rewardName: reward.name,
          categoryId: reward.categoryId,
          categoryName: reward.categoryName,
          categoryDisplayOrder: reward.categoryDisplayOrder,
          rewardDisplayOrder: reward.displayOrder ?? 0,
          selected: true, // All package rewards are selected by default
          companyContribution: existingItem?.companyContribution ?? packageItem?.companyContribution ?? 0,
          employeeContribution: existingItem?.employeeContribution ?? packageItem?.employeeContribution ?? 0,
          supportsEmployeeContribution: reward.supportsEmployeeContribution,
        })
      })
      setRewardItems(items)
      setIsInitialized(true)
    }
  }, [allRewards, userRewards, isInitialized, isLoadingUserRewards, packageInfo])

  const handleToggleReward = useCallback((rewardId: string) => {
    setRewardItems((prev) => {
      const next = new Map(prev)
      const item = next.get(rewardId)
      if (item) {
        next.set(rewardId, { ...item, selected: !item.selected })
      }
      return next
    })
  }, [])

  const handleContributionChange = useCallback(
    (rewardId: string, field: 'companyContribution' | 'employeeContribution', value: string) => {
      // Strip commas and parse the number
      const numericValue = parseFloat(value.replace(/,/g, '')) || 0
      setRewardItems((prev) => {
        const next = new Map(prev)
        const item = next.get(rewardId)
        if (item) {
          next.set(rewardId, {
            ...item,
            [field]: numericValue,
          })
        }
        return next
      })
    },
    []
  )

  // Format number with commas
  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  }

  const handleNext = useCallback(() => {
    setActiveStep((prev) => prev + 1)
  }, [])

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1)
  }, [])

  const handleSubmit = async () => {
    if (!userId || !periodId || !rootOrganizationId || !rootPeriodId || !packageInfo?.id) return

    setIsSubmitting(true)
    try {
      const selectedRewards: UserRewardItem[] = []
      rewardItems.forEach((item) => {
        if (item.selected) {
          selectedRewards.push({
            rewardId: item.rewardId,
            rewardName: item.rewardName,
            rewardDisplayOrder: item.rewardDisplayOrder,
            categoryId: item.categoryId,
            categoryName: item.categoryName,
            companyContribution: item.companyContribution,
            employeeContribution: item.employeeContribution,
          })
        }
      })

      await saveUserRewards({
        userId,
        periodId,
        rootOrganizationId,
        organizationId: rootOrganizationId,
        rootPeriodId,
        rewards: selectedRewards,
        packageId: packageInfo.id,
      })
      enqueueSnackbar('Employee rewards saved successfully', { variant: 'success' })
      invalidateUserRewardsCache(userId, periodId, token!)
      invalidateEmployeeRewardsSummariesCache(rootOrganizationId, periodId, token!)
      startTransition(() => navigate('/rewards/assign'))
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to save employee rewards'
      enqueueSnackbar(errorMessage, { variant: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Get selected rewards for review
  const selectedRewards = useMemo(() => {
    const selected: RewardItemState[] = []
    rewardItems.forEach((item) => {
      if (item.selected) {
        selected.push(item)
      }
    })
    // Sort by category display order asc, then by reward display order asc
    selected.sort((a, b) => {
      const catCompare = a.categoryDisplayOrder - b.categoryDisplayOrder
      if (catCompare !== 0) return catCompare
      return a.rewardDisplayOrder - b.rewardDisplayOrder
    })
    return selected
  }, [rewardItems])

  // Calculate totals
  const totals = useMemo(() => {
    let company = 0
    let employee = 0
    selectedRewards.forEach((item) => {
      company += item.companyContribution
      employee += item.employeeContribution
    })
    return { company, employee, total: company + employee }
  }, [selectedRewards])

  // Group rewards by category for card display (step 1)
  const rewardsByCategory = useMemo(() => {
    const categories = new Map<
      string,
      { categoryName: string; categoryDisplayOrder: number; rewards: RewardItemState[] }
    >()

    allRewards.forEach((reward) => {
      const item = rewardItems.get(reward.id!)
      if (!item) return

      if (!categories.has(reward.categoryName)) {
        categories.set(reward.categoryName, {
          categoryName: reward.categoryName,
          categoryDisplayOrder: reward.categoryDisplayOrder,
          rewards: [],
        })
      }
      categories.get(reward.categoryName)!.rewards.push(item)
    })

    // Sort rewards within each category by displayOrder
    categories.forEach((category) => {
      category.rewards.sort((a, b) => a.rewardDisplayOrder - b.rewardDisplayOrder)
    })

    // Convert to array and sort by category display order
    return Array.from(categories.values()).sort((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder)
  }, [allRewards, rewardItems])

  // Group selected rewards by category for review step (step 2)
  const selectedRewardsByCategory = useMemo(() => {
    const categories = new Map<
      string,
      { categoryName: string; categoryDisplayOrder: number; rewards: RewardItemState[] }
    >()

    selectedRewards.forEach((item) => {
      if (!categories.has(item.categoryName)) {
        categories.set(item.categoryName, {
          categoryName: item.categoryName,
          categoryDisplayOrder: item.categoryDisplayOrder,
          rewards: [],
        })
      }
      categories.get(item.categoryName)!.rewards.push(item)
    })

    // Sort rewards within each category by displayOrder
    categories.forEach((category) => {
      category.rewards.sort((a, b) => a.rewardDisplayOrder - b.rewardDisplayOrder)
    })

    // Convert to array and sort by category display order
    return Array.from(categories.values()).sort((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder)
  }, [selectedRewards])

  // Get employee name from user rewards if available
  const employeeName = useMemo(() => {
    if (userRewards && userRewards.length > 0) {
      // We don't have employee name from user rewards, so we'll just show "Employee"
      return 'Employee'
    }
    return 'Employee'
  }, [userRewards])

  const isLoading = isLoadingRewards || isLoadingUserRewards || !isInitialized

  // Show error if missing required params
  if (!userId || !periodId) {
    return (
      <>
        <Grid container pb={2}>
          <Grid size={{ xs: 12 }}>
            <SimpleTitle title="Edit Employee Rewards" />
          </Grid>
        </Grid>
        <Paper sx={{ p: 3 }}>
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">Missing required parameters</Typography>
            <Button
              variant="contained"
              onClick={() => startTransition(() => navigate('/rewards/assign'))}
              sx={{ mt: 2 }}
            >
              Back to Employee Rewards
            </Button>
          </Box>
        </Paper>
      </>
    )
  }

  const renderStep1 = () => (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : allRewards.length === 0 ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography color="textSecondary" textAlign="center" py={4}>
            No rewards available. Create rewards first in Manage Rewards.
          </Typography>
        </Paper>
      ) : (
        <>
          {rewardsByCategory.map((category, index) => (
            <PackageCategoryCard
              key={category.categoryName}
              categoryName={category.categoryName}
              rewards={category.rewards}
              onToggleRewardSelect={handleToggleReward}
              onContributionChange={handleContributionChange}
              formatCurrency={formatCurrency}
              theme={theme}
              disableDragAndDrop={true}
              hideSelection={true}
            />
          ))}
        </>
      )}
    </>
  )

  const renderStep2 = () => (
    <>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          <strong>Assignment Details</strong>
        </Typography>
        <Typography>
          <strong>Employee:</strong> {employeeName}
        </Typography>
        <Typography sx={{ mt: 1 }}>
          <strong>Period:</strong> {periodInfo?.label || periodId}
        </Typography>
        <Typography sx={{ mt: 1 }}>
          <strong>Package:</strong> {packageInfo?.name || 'N/A'}
        </Typography>
        <Typography sx={{ mt: 1 }}>
          <strong>Rewards:</strong> {selectedRewards.length}
        </Typography>
      </Paper>

      {selectedRewards.length === 0 ? (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography color="textSecondary" textAlign="center" py={2}>
            No rewards selected
          </Typography>
        </Paper>
      ) : (
        <>
          {selectedRewardsByCategory.map((category) => (
            <ReviewCategoryCard
              key={category.categoryName}
              categoryName={category.categoryName}
              rewards={category.rewards}
              theme={theme}
            />
          ))}

          <Paper sx={{ p: 2, mt: 2, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={1} px={1.5}>
              <Box sx={{ flexGrow: 1, minWidth: 120 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                  Totals
                </Typography>
              </Box>
              <Box sx={{ width: 140, textAlign: 'right', pr: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                  ${totals.company.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ width: 140, textAlign: 'right', pr: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                  ${totals.employee.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </>
      )}
    </>
  )

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Edit Employee Rewards" />
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {activeStep === 0 && renderStep1()}
      {activeStep === 1 && renderStep2()}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <AnimateButton>
            <Button
              variant="contained"
              color="warning"
              onClick={() => startTransition(() => navigate('/rewards/assign'))}
            >
              Cancel
            </Button>
          </AnimateButton>

          <Stack direction="row" alignItems="center" spacing={2}>
            {activeStep > 0 && (
              <Button variant="outlined" onClick={handleBack}>
                Back
              </Button>
            )}

            {activeStep < steps.length - 1 ? (
              <AnimateButton>
                <Button variant="contained" onClick={handleNext} disabled={isLoading}>
                  Next
                </Button>
              </AnimateButton>
            ) : (
              <AnimateButton>
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Save Rewards'}
                </Button>
              </AnimateButton>
            )}
          </Stack>
        </Stack>
      </Paper>
    </>
  )
}

export default EditEmployeeRewards
